import React from 'react';
import { App } from 'antd';

import TagBadge from './TagBadge';
import RecordBadge from '../common/RecordBadge';
import useActiveTag from '../../hooks/useActiveTag';
import {
	ADDING_TO_LABEL,
	STOP_ADDING_LABEL,
	activeTagDates,
	activeTagRefusal,
} from '../../utils/activeTag';
import { tagPagePath, tagRecordOf } from '../../utils/tags';

/**
 * "Adding to *tag* ✕" — THE ACTIVE TAG in the shell (docs/plans/
 * tags-active-tag-proposal.md; build call b49): a quiet line in the rail's
 * foot while one of the person's private tags is active, so it can't be
 * forgotten. The chip is the tag's own badge (docs/platform-standards.md
 * § Tags: nothing inside the pill), the door to its tag page; its ✕ is
 * **Stop adding it** (a named button, 44px on a touch screen — tags.css).
 * Below it, when it started and, if it has one, when it ends: "since Sep 29 ·
 * until Oct 31".
 *
 * Collapsed, the rail has room for the tag's tile alone: its tooltip says the
 * rest, and it opens the tag page, whose ⋯ has Stop adding it ⚑ (no ✕ there,
 * like Recent's forget).
 *
 * Nothing renders while no tag is active — and nothing is asked for: the
 * private tags load only when a tag is active (hooks/useActiveTag). On a
 * phone the rail is gone and the same words sit in the Me menu (Layout.js).
 */
export default function ActiveTagChip({ collapsed = false }) {
	const { message } = App.useApp();
	const { activeTag, stop } = useActiveTag();
	if (!activeTag) return null;

	const { tag } = activeTag;
	const name = tag.name || 'this tag';
	const dates = activeTagDates(activeTag);
	const to = tagPagePath(tag) || undefined;

	const stopAdding = async () => {
		try {
			await stop();
			message.success(`Stopped adding “${name}”.`);
		} catch (error) {
			message.error(activeTagRefusal(error));
		}
	};

	if (collapsed) {
		return (
			<div className="active-tag active-tag--collapsed" data-testid="active-tag-chip">
				<RecordBadge
					kind="tag"
					record={tagRecordOf(tag)}
					variant="plain"
					size="xs"
					tileSize={21}
					title={null}
					tooltip={[`${ADDING_TO_LABEL} ${name}`, dates].filter(Boolean).join(' · ')}
					aria-label={`${ADDING_TO_LABEL} ${name}`}
					to={to}
					block
					className="rail__pin active-tag__tile"
				/>
			</div>
		);
	}

	return (
		<div className="active-tag" data-testid="active-tag-chip">
			<div className="active-tag__line">
				<span className="active-tag__lead">{ADDING_TO_LABEL}</span>
				<TagBadge tag={tag} size="xs" to={to} onRemove={stopAdding} removeLabel={STOP_ADDING_LABEL} />
			</div>
			{dates ? <div className="active-tag__dates">{dates}</div> : null}
		</div>
	);
}

/**
 * The same, for the phone's Me menu (the one shell menu below md): an antd
 * menu group — the chip (opening the tag page) and **Stop adding it** — or an
 * empty list while no tag is active. `onOpen(path)` and `onStop()` are the
 * menu's clicks, dispatched by key (`ACTIVE_TAG_MENU_KEYS`).
 */
export const ACTIVE_TAG_MENU_KEYS = Object.freeze({ open: 'active-tag-open', stop: 'active-tag-stop' });

export function activeTagMenuItems(activeTag, { closeIcon = null } = {}) {
	if (!activeTag?.tag) return [];
	const dates = activeTagDates(activeTag);
	return [
		{
			type: 'group',
			key: 'active-tag',
			label: ADDING_TO_LABEL,
			children: [
				{
					key: ACTIVE_TAG_MENU_KEYS.open,
					className: 'account-menu__active-tag',
					label: (
						<span className="account-menu__active-tag-chip">
							<TagBadge tag={activeTag.tag} size="xs" />
							{dates ? <span className="account-menu__active-tag-dates">{dates}</span> : null}
						</span>
					),
				},
				{
					key: ACTIVE_TAG_MENU_KEYS.stop,
					className: 'account-menu__active-tag-stop',
					icon: closeIcon,
					label: STOP_ADDING_LABEL,
				},
			],
		},
		{ type: 'divider', key: 'active-tag-divider' },
	];
}
